/**
 * ESCHER FALLS — レベルJSONの検証付きパース。MODULE-CONTRACTS §9。
 *
 * `unknown` を受け取り、構造を全て検査してから `Level` を返す。
 * three.js に依存しない。ネットワーク/ファイル I/O も行わない
 * （JSON は `import` 済みの値として渡される）。
 *
 * 検証しないもの（`tools/validate-levels.ts` の責務）:
 * 解の視点の個数 / `startView` が解の視点でないか / `waterBudget` の充足 /
 * セル・ポートの重なり。ローダはこれらを素通しする。
 */

import type { Axis6, Azimuth, Elevation, Level, PieceDef, PieceType, Turn, Vec3, ViewPose } from '../core/types';

// =============================================================================
// エラー型
// =============================================================================

/** レベルJSONの構造が不正なときに投げられる。 */
export class LevelParseError extends Error {
  /** 問題のあった位置。例: `"pieces[3].dir"`。レベル全体なら `""`。 */
  readonly path: string;
  /** どのファイル/レベル由来か。不明なら `"(unknown)"`。 */
  readonly source: string;

  constructor(message: string, path: string, source: string) {
    super(message);
    this.name = 'LevelParseError';
    this.path = path;
    this.source = source;
  }
}

// =============================================================================
// 定数
// =============================================================================

const AXES: readonly Axis6[] = ['+x', '-x', '+y', '-y', '+z', '-z'];

const PIECE_TYPES: readonly PieceType[] = [
  'source',
  'straight',
  'corner',
  'fall',
  'waterwheel',
  'goal',
  'drain',
  'pillar',
  'turntable',
  'lever',
];

const UNKNOWN_SOURCE = '(unknown)';

// =============================================================================
// 小さなヘルパー
// =============================================================================

type Rec = Record<string, unknown>;

function isRecord(value: unknown): value is Rec {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isAxis6(value: unknown): value is Axis6 {
  return typeof value === 'string' && (AXES as readonly string[]).includes(value);
}

function isPieceType(value: unknown): value is PieceType {
  return typeof value === 'string' && (PIECE_TYPES as readonly string[]).includes(value);
}

/** `'+x'` → `'x'`。`corner` の「異なる軸」判定に使う。 */
function axisLetter(axis: Axis6): 'x' | 'y' | 'z' {
  return axis[1] as 'x' | 'y' | 'z';
}

/** 値を日本語エラーメッセージに埋め込むための短い表現。 */
function show(value: unknown): string {
  if (value === undefined) return '未指定';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return String(value);
  }
  if (Array.isArray(value)) return `[${value.map(show).join(', ')}]`;
  return typeof value;
}

/** 常に投げる。戻り値 `never` なので呼び出し側で制御フローが絞れる。 */
function fail(source: string, path: string, message: string): never {
  const where = path === '' ? source : `${source} の ${path}`;
  throw new LevelParseError(`${where}: ${message}`, path, source);
}

function readNonEmptyString(rec: Rec, key: string, source: string, path: string): string {
  const value = rec[key];
  if (typeof value !== 'string' || value.length === 0) {
    fail(source, path, `非空の文字列でなければならない（実際: ${show(value)}）`);
  }
  return value;
}

function readPositiveInt(rec: Rec, key: string, source: string, path: string): number {
  const value = rec[key];
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
    fail(source, path, `1 以上の整数でなければならない（実際: ${show(value)}）`);
  }
  return value;
}

function readAxis(rec: Rec, key: string, source: string, path: string): Axis6 {
  const value = rec[key];
  if (!isAxis6(value)) {
    fail(source, path, `方向は ${AXES.join(' / ')} のいずれか（実際: ${show(value)}）`);
  }
  return value;
}

function readPos(rec: Rec, source: string, path: string): Vec3 {
  const value = rec['pos'];
  if (!Array.isArray(value) || value.length !== 3) {
    fail(source, path, `長さ 3 の配列でなければならない（実際: ${show(value)}）`);
  }
  const out: number[] = [];
  for (let i = 0; i < 3; i++) {
    const c = value[i];
    if (typeof c !== 'number' || !Number.isInteger(c)) {
      // 非整数の pos はポート座標の「.5 成分は高々1個」を破り誤接続を生む
      // （PROJECTION-VERIFIED §2.4）。ここで確実に弾く。
      fail(source, `${path}[${i}]`, `座標は整数でなければならない（実際: ${show(c)}）`);
    }
    out.push(c);
  }
  return [out[0], out[1], out[2]];
}

// =============================================================================
// ピース1つのパース
// =============================================================================

function parsePiece(raw: unknown, index: number, source: string): PieceDef {
  const base = `pieces[${index}]`;
  if (!isRecord(raw)) {
    fail(source, base, `ピースはオブジェクトでなければならない（実際: ${show(raw)}）`);
  }

  const type = raw['type'];
  if (!isPieceType(type)) {
    fail(
      source,
      `${base}.type`,
      `未知のピース種別。${PIECE_TYPES.join(' / ')} のいずれか（実際: ${show(type)}）`,
    );
  }

  const pos = readPos(raw, source, `${base}.pos`);

  // id は省略可。書くなら非空文字列。
  let id: string | undefined;
  if (raw['id'] !== undefined && raw['id'] !== null) {
    id = readNonEmptyString(raw, 'id', source, `${base}.id`);
  }

  // レバーが入っているときの変位。無ければ省略する。
  let shift: Vec3 | undefined;
  if (raw['shift'] !== undefined && raw['shift'] !== null) {
    const v = raw['shift'];
    if (!Array.isArray(v) || v.length !== 3) {
      fail(source, `${base}.shift`, `長さ 3 の配列でなければならない（実際: ${show(v)}）`);
    }
    for (let i = 0; i < 3; i++) {
      if (typeof v[i] !== 'number' || !Number.isInteger(v[i])) {
        // 非整数の変位はポート座標の不変条件（.5 はちょうど1個）を壊す。
        fail(source, `${base}.shift[${i}]`, `変位は整数（実際: ${show(v[i])}）`);
      }
    }
    shift = [v[0], v[1], v[2]];
  }
  // レバーが入っているときの回転（回る足場）。無ければ省略する。
  let turn: Turn | undefined;
  if (raw['turn'] !== undefined && raw['turn'] !== null) {
    const v = raw['turn'];
    if (!isRecord(v)) fail(source, `${base}.turn`, `{ about: [x, z], quarters } でなければならない（実際: ${show(v)}）`);
    const about = v['about'];
    if (
      !Array.isArray(about) || about.length !== 2 ||
      !about.every((n) => typeof n === 'number' && Number.isInteger(n))
    ) {
      // 軸が半端だと、回したあとの pos が整数でなくなり、ポートの不変条件が壊れる。
      fail(source, `${base}.turn.about`, `長さ 2 の整数配列 [x, z]（実際: ${show(about)}）`);
    }
    const q = v['quarters'];
    if (q !== 1 && q !== 2 && q !== 3) {
      fail(source, `${base}.turn.quarters`, `1 / 2 / 3 のいずれか（実際: ${show(q)}）`);
    }
    if (shift !== undefined) {
      fail(source, `${base}.turn`, `shift と turn は同じピースに併用できない`);
    }
    turn = { about: [about[0], about[1]], quarters: q };
  }
  const withShift = <T extends PieceDef>(x: T): T => {
    let y: T = shift === undefined ? x : { ...x, shift };
    if (turn !== undefined) y = { ...y, turn };
    return y;
  };

  // 未知のキーはエラーにしない（前方互換のため無視する）。
  switch (type) {
    case 'source':
    case 'goal':
    // 涸れ井戸はゴールと同じ形（向きだけ）。読み方を分ける理由が無い。
    case 'drain': {
      const dir = readAxis(raw, 'dir', source, `${base}.dir`);
      return withShift(id === undefined ? { type, pos, dir } : { type, pos, dir, id });
    }
    case 'straight': {
      const dir = readAxis(raw, 'dir', source, `${base}.dir`);
      const length = readPositiveInt(raw, 'length', source, `${base}.length`);
      return withShift(
        id === undefined ? { type, pos, dir, length } : { type, pos, dir, length, id },
      );
    }
    case 'corner': {
      const from = readAxis(raw, 'from', source, `${base}.from`);
      const to = readAxis(raw, 'to', source, `${base}.to`);
      if (axisLetter(from) === axisLetter(to)) {
        fail(
          source,
          `${base}.to`,
          `corner の from と to は異なる軸でなければならない`
            + `（${show(from)} と ${show(to)} はどちらも ${axisLetter(from)} 軸）`,
        );
      }
      return withShift(id === undefined ? { type, pos, from, to } : { type, pos, from, to, id });
    }
    case 'fall': {
      const height = readPositiveInt(raw, 'height', source, `${base}.height`);
      return withShift(id === undefined ? { type, pos, height } : { type, pos, height, id });
    }
    case 'pillar': {
      const height = readPositiveInt(raw, 'height', source, `${base}.height`);
      return withShift(id === undefined ? { type, pos, height } : { type, pos, height, id });
    }
    case 'lever': {
      // レバー自体は動かない（shift を持たせても意味が無いので受け付けるだけ）。
      return withShift(id === undefined ? { type, pos } : { type, pos, id });
    }
    case 'turntable': {
      const radius = readPositiveInt(raw, 'radius', source, `${base}.radius`);
      return withShift(id === undefined ? { type, pos, radius } : { type, pos, radius, id });
    }
    case 'waterwheel': {
      // dir は省略可（既定 '+y'）。省略されていたら省略のまま残す。
      if (raw['dir'] === undefined || raw['dir'] === null) {
        return id === undefined ? { type, pos } : { type, pos, id };
      }
      const dir = readAxis(raw, 'dir', source, `${base}.dir`);
      return withShift(id === undefined ? { type, pos, dir } : { type, pos, dir, id });
    }
  }
}

// =============================================================================
// レベルのパース
// =============================================================================

/** `parseLevels` が各要素に付ける source ラベル。 */
function sourceLabel(raw: unknown, index: number): string {
  if (isRecord(raw) && typeof raw['id'] === 'string' && raw['id'].length > 0) {
    return raw['id'];
  }
  return `levels[${index}]`;
}


/**
 * 開始姿勢を読む。**旧形式（数値 = 方位、仰角 0）も受け付ける。**
 *
 * 視点を4つから12姿勢に拡張したとき、既存8面の JSON を書き換えずに動かすために
 * 両形式を許している。新しい面は `{ "azimuth": n, "elevation": n }` で書くこと。
 */
function parseStartView(value: unknown, source: string): ViewPose {
  if (typeof value === 'number') {
    if (!Number.isInteger(value) || value < 0 || value > 3) {
      fail(source, 'startView', `開始視点を数値で書くときは 0〜3 の整数（実際: ${show(value)}）`);
    }
    return { azimuth: value as Azimuth, elevation: 0 };
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    fail(
      source,
      'startView',
      `開始姿勢は 0〜3 の数値、または { azimuth, elevation } （実際: ${show(value)}）`,
    );
  }
  const obj = value as Record<string, unknown>;
  const az = obj['azimuth'];
  const el = obj['elevation'];
  if (typeof az !== 'number' || !Number.isInteger(az) || az < 0 || az > 3) {
    fail(source, 'startView.azimuth', `方位は 0〜3 の整数（実際: ${show(az)}）`);
  }
  if (typeof el !== 'number' || !Number.isInteger(el) || el < 0 || el > 2) {
    fail(source, 'startView.elevation', `仰角は 0〜2 の整数（実際: ${show(el)}）`);
  }
  return { azimuth: az as Azimuth, elevation: el as Elevation };
}

/**
 * 未検証の値を `Level` に変換する。
 *
 * @param raw    レベルJSONを import した値など
 * @param source エラーメッセージに出す出所。省略時は `"(unknown)"`
 * @throws {LevelParseError} 構造が不正なとき（最初に見つかった1件で投げる）
 */
export function parseLevel(raw: unknown, source: string = UNKNOWN_SOURCE): Level {
  if (!isRecord(raw)) {
    fail(source, '', `レベルはオブジェクトでなければならない（実際: ${show(raw)}）`);
  }

  const id = readNonEmptyString(raw, 'id', source, 'id');
  const name = readNonEmptyString(raw, 'name', source, 'name');

  const startView = parseStartView(raw['startView'], source);

  let waterBudget: number | null = null;
  if (raw['waterBudget'] !== undefined && raw['waterBudget'] !== null) {
    waterBudget = readPositiveInt(raw, 'waterBudget', source, 'waterBudget');
  }

  let timeLimitMs: number | null = null;
  if (raw['timeLimitMs'] !== undefined && raw['timeLimitMs'] !== null) {
    timeLimitMs = readPositiveInt(raw, 'timeLimitMs', source, 'timeLimitMs');
  }

  let maxMoves: number | null = null;
  if (raw['maxMoves'] !== undefined && raw['maxMoves'] !== null) {
    maxMoves = readPositiveInt(raw, 'maxMoves', source, 'maxMoves');
  }

  let hint: string | null = null;
  if (raw['hint'] !== undefined && raw['hint'] !== null) {
    const value = raw['hint'];
    if (typeof value !== 'string') {
      fail(source, 'hint', `ヒントは文字列でなければならない（実際: ${show(value)}）`);
    }
    hint = value;
  }

  const rawPieces = raw['pieces'];
  if (!Array.isArray(rawPieces)) {
    fail(source, 'pieces', `pieces は配列でなければならない（実際: ${show(rawPieces)}）`);
  }
  if (rawPieces.length === 0) {
    fail(source, 'pieces', 'ピースが 1 つもない');
  }

  const pieces: PieceDef[] = rawPieces.map((p, i) => parsePiece(p, i, source));

  // --- レベル全体の整合 ---------------------------------------------------

  // ID の重複（明示 ID と自動採番 `p{index}` は同じ名前空間を共有する）。
  const seen = new Map<string, number>();
  pieces.forEach((def, i) => {
    const effective = def.id ?? `p${i}`;
    const prev = seen.get(effective);
    if (prev !== undefined) {
      fail(
        source,
        `pieces[${i}].id`,
        `ピースID ${show(effective)} が pieces[${prev}] と重複している`
          + `（id を省略したピースには "p{添字}" が自動採番される）`,
      );
    }
    seen.set(effective, i);
  });

  const sources = pieces.filter((p) => p.type === 'source').length;
  if (sources !== 1) {
    fail(source, 'pieces', `source はちょうど 1 つ必要（実際: ${sources} 個）`);
  }
  // ゴールは複数置ける。**全てに水が届いて初めてクリア**になる（METAGAME-SPEC §5）。
  const goals = pieces.filter((p) => p.type === 'goal').length;
  if (goals < 1) {
    fail(source, 'pieces', 'goal が 1 つも無い');
  }

  return { id, name, startView, waterBudget, timeLimitMs, maxMoves, hint, pieces };
}

/**
 * 配列をまとめてパースする。1件でも失敗したら投げる。
 * 各要素の `source` は、その要素の `id` があればそれ、無ければ `levels[i]`。
 */
export function parseLevels(raws: readonly unknown[]): Level[] {
  return raws.map((raw, i) => parseLevel(raw, sourceLabel(raw, i)));
}

/**
 * `parseLevel` の別名。
 *
 * MODULE-CONTRACTS §9 の正式名は `parseLevel` だが、本モジュールの呼び名として
 * `loadLevel` を指定された経緯があるため、同義の薄いラッパを置いてある。
 * 新規コードは `parseLevel` を使うこと。
 */
export function loadLevel(raw: unknown, source?: string): Level {
  return parseLevel(raw, source);
}
